import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";

import {
  selectDeleteTrainModelPending,
  selectSelectedTrainModel,
  selectUpdateTrainModelLoading,
} from "../../store/slices/model/slice";
import {
  getTrainModel,
  updateTrainModel,
  deleteTrainModel,
} from "../../store/slices/model/asyncThunks";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  TutorialLink,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  RequirementText,
  UploadSection,
  UploadText,
  UploadCount,
  ActionButtons,
  DeleteButton,
  InputContainer,
  Row,
  Text,
  LabelContainer,
  LabelUpload,
  LabelRow,
  FilePreviewGrid,
  FilePreviewItem,
  FilePreview,
  RemoveFileButton,
  FileName,
  SkeletonLoader,
  SkeletonSection,
  SkeletonLoaderColumn,
  UploadCard2,
  UploadDescriptionText,
  ImgContainer,
  DatasetHeaderLeft,
  DatasetGrid,
  DatasetRow,
  DatasetRowNumber,
  DatasetRowContent,
  DatasetColumn,
  DatasetColumnLabel,
  DatasetArrow,
  DatasetUploadCard,
  DatasetUploadText,
  DatasetUploadSubtext,
  DatasetRemoveRowButton,
  DatasetRemoveFileButton,
  SectionHeader,
  SectionWrapper,
  LabelColumn,
  LinkText,
  Buttons,
  ButtonDuration,
  CheckboxRow,
  CheckboxLabel,
  TextAreaInput,
  AddSetContainer,
} from "./TrainingPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as SettingsSvg } from "../../assets/icons/settings.svg";
import { ReactComponent as CardIconSvg } from "../../assets/icons/cardIcon.svg";
import { ReactComponent as VideoSvg } from "../../assets/icons/video.svg";
import { ReactComponent as ImageSvg } from "../../assets/icons/image.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/whiteStar.svg";
import { ReactComponent as ImagesSvg } from "../../assets/icons/AddMedia.svg";
import { ReactComponent as PlusSvg } from "../../assets/icons/plus.svg";
import { ReactComponent as UploadSvg } from "../../assets/icons/upload.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as CloseSvg } from "../../assets/icons/close.svg";
// import { ReactComponent as WarningIcon } from "../../assets/icons/warning.svg";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import Button from "../../components/utils/Button/Button";
import { toast } from "../../components/modern-ui/sonner";
import GalleryModal from "../../components/GalleryModal/GalleryModal";
import modelService from "../../api/services/model";
import TrainingProgressComponent from "../../components/utils/TrainingProgressComponent/TrainingProgressComponent";
import { selectUser } from "../../store/slices/user/slice";
import AnonymizeModal from "../../components/AnonymizeModal/AnonymizeModal";

// import likeAnimation from "../../assets/animations/like2.json";
import deleteAnimation from "../../assets/animations/trash.json";
import checkboxAnimation from "../../assets/animations/radioButton.json";
import LottieIcon from "../../components/LottieIcon/LottieIcon";

const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_DATASET_ROWS = 50;
const DURATION_OPTIONS = [5, 8];

const TrainingPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const trainModelId = params.modelId;
  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const updateTrainModelLoading = useSelector(selectUpdateTrainModelLoading);
  const deleteTrainModelLoading = useSelector(selectDeleteTrainModelPending);
  const isLoggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);

  // Training type: "video" | "image" | "person"
  const trainingType = selectedTrainModel.data?.type || "video";

  const [prompt, setPrompt] = useState("");
  const [audioPrompt, setAudioPrompt] = useState("");
  const [taskName, setTaskName] = useState(selectedTrainModel.data?.name || "");
  const [uploadedVideoFiles, setUploadedVideoFiles] = useState([]);
  const [uploadedImageFiles, setUploadedImageFiles] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReadOnlyMode, setIsReadOnlyMode] = useState(false);
  const [openAnonymizeModal, setOpenAnonymizeModal] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [areDataLoadedFromLocalStorage, setAreDataLoadedFromLocalStorage] =
    useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [anonymizeSelectImages, setAnonymizeSelectImages] = useState(null);

  const [collectionGroup, setCollectionGroup] = useState(null);

  const datasetHeaderRef = useRef(null);

  const [videoDuration, setVideoDuration] = useState(5);
  const [is4kDataset, setIs4kDataset] = useState(false);
  const activeIndex = DURATION_OPTIONS.indexOf(videoDuration);

  // Dataset pairs state (Train Image mode)
  // Each pair: { id, before: fileObj | null, after: fileObj | null }
  const [datasetPairs, setDatasetPairs] = useState([
    { id: 1, before: null, after: null },
    { id: 2, before: null, after: null },
    { id: 3, before: null, after: null },
    { id: 4, before: null, after: null },
    { id: 5, before: null, after: null },
    { id: 6, before: null, after: null },
    { id: 7, before: null, after: null },
    { id: 8, before: null, after: null },
    { id: 9, before: null, after: null },
    { id: 10, before: null, after: null },
    { id: 11, before: null, after: null },
    { id: 12, before: null, after: null },
    { id: 13, before: null, after: null },
    { id: 14, before: null, after: null },
    { id: 15, before: null, after: null },
  ]);

  // Person mode images state
  const [personImages, setPersonImages] = useState([]);

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  useEffect(() => {
    if (updateTrainModelLoading) {
      setIsReadOnlyMode(true);
    }
  }, [updateTrainModelLoading]);

  const hasSyncedRef = useRef(false);

  // =============================================
  // Save to localStorage whenever state changes
  // =============================================

  useEffect(() => {
    if (!areDataLoadedFromLocalStorage || !trainModelId) return;
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      return;
    }

    const storageData = {
      prompt,
      taskName,
    };

    if (trainingType === "video") {
      storageData.imagesUrl = uploadedImageFiles
        .filter((v) => v.isUploaded && v.serverUrl)
        .map((v) => v.serverUrl);
      storageData.selectedImages = selectedImages;
      storageData.videosUrl = uploadedVideoFiles
        .filter((v) => v.isUploaded && v.serverUrl)
        .map((v) => v.serverUrl);
      storageData.videoLength = videoDuration ?? 5;
      storageData.is4kDataset = is4kDataset;
      storageData.audioPrompt = audioPrompt;
    } else if (trainingType === "image") {
      storageData.datasetPairs = datasetPairs.map((pair) => ({
        id: pair.id,
        before:
          pair.before?.isUploaded && pair.before?.serverUrl
            ? pair.before.serverUrl
            : null,
        after:
          pair.after?.isUploaded && pair.after?.serverUrl
            ? pair.after.serverUrl
            : null,
      }));
      storageData.imagesUrl = uploadedImageFiles
        .filter((v) => v.isUploaded && v.serverUrl)
        .map((v) => v.serverUrl);
      storageData.selectedImages = selectedImages;
    } else if (trainingType === "person") {
      storageData.personImagesUrl = personImages
        .filter((v) => v.isUploaded && v.serverUrl)
        .map((v) => v.serverUrl);
    }

    localStorage.setItem(
      "train-files-" + trainModelId,
      JSON.stringify(storageData),
    );
  }, [
    areDataLoadedFromLocalStorage,
    prompt,
    audioPrompt,
    selectedImages,
    taskName,
    trainModelId,
    trainingType,
    uploadedImageFiles,
    uploadedVideoFiles,
    datasetPairs,
    personImages,
    videoDuration,
    is4kDataset,
  ]);

  // =============================================
  // Load from server data or localStorage
  // =============================================

  useEffect(() => {
    if (
      selectedTrainModel.data &&
      trainModelId === selectedTrainModel.trainModelId
    ) {
      const model = selectedTrainModel.data;

      const hasExistingFiles =
        (model.imageUrls && model.imageUrls.length > 0) ||
        (model.videoUrls && model.videoUrls.length > 0) ||
        (model.datasetPairs && model.datasetPairs.length > 0) ||
        (model.personImageUrls && model.personImageUrls.length > 0);

      setIsReadOnlyMode(hasExistingFiles);

      if (hasExistingFiles) {
        // Model already submitted — load server data, clear localStorage
        localStorage.removeItem("train-files-" + trainModelId);
        setPrompt(model.prompt || "");
        setAudioPrompt(model.audioPrompt || "");
        setTaskName(model.name || "");

        if (trainingType === "video") {
          const existingImageFiles = [];
          const existingVideoFiles = [];

          if (model.imageUrls?.length > 0) {
            model.imageUrls.forEach((url, index) => {
              existingImageFiles.push({
                id: `existing-image-${index}`,
                file: null,
                name: `Image ${index + 1}`,
                type: "image/jpeg",
                isExisting: true,
                serverUrl: url,
                isUploaded: true,
              });
            });
          }

          if (model.videoUrls?.length > 0) {
            model.videoUrls.forEach((url, index) => {
              existingVideoFiles.push({
                id: `existing-video-${index}`,
                file: null,
                name: `Video ${index + 1}`,
                type: "video/mp4",
                isExisting: true,
                serverUrl: url,
                isUploaded: true,
              });
            });
          }
          setVideoDuration(model.videoLength ?? 5);
          setIs4kDataset(Boolean(model.is4kDataset));
          setUploadedImageFiles(existingImageFiles);
          setUploadedVideoFiles(existingVideoFiles);
        } else if (trainingType === "image") {
          // Also load testing images for image mode
          const existingTestingImages = [];
          if (model.imageUrls?.length > 0) {
            model.imageUrls.forEach((url, index) => {
              existingTestingImages.push({
                id: `existing-testing-${index}`,
                file: null,
                name: `Image ${index + 1}`,
                type: "image/jpeg",
                isExisting: true,
                serverUrl: url,
                isUploaded: true,
              });
            });
          }
          setUploadedImageFiles(existingTestingImages);
        } else if (
          trainingType === "person" &&
          model.personImageUrls?.length > 0
        ) {
          const existingPersonImages = model.personImageUrls.map(
            (url, index) => ({
              id: `existing-person-${index}`,
              file: null,
              name: `Image ${index + 1}`,
              type: "image/jpeg",
              isExisting: true,
              serverUrl: url,
              isUploaded: true,
            }),
          );
          setPersonImages(existingPersonImages);
        }
      } else {
        // No server data — restore from localStorage (only once per mount)
        const savedFiles = JSON.parse(
          localStorage.getItem("train-files-" + model._id),
        );

        if (trainingType === "video") {
          if (!areDataLoadedFromLocalStorage) {
            const existingImageFiles = [];
            const existingVideoFiles = [];

            if (savedFiles?.imagesUrl) {
              savedFiles.imagesUrl.forEach((url, index) => {
                existingImageFiles.push({
                  id: `existing-image-${index}`,
                  file: null,
                  name: `Image ${index + 1}`,
                  type: "image/jpeg",
                  isExisting: true,
                  serverUrl: url,
                  isUploaded: true,
                });
              });
            }

            if (savedFiles?.videosUrl) {
              savedFiles.videosUrl.forEach((url, index) => {
                existingVideoFiles.push({
                  id: `existing-video-${index}`,
                  file: null,
                  name: `Video ${index + 1}`,
                  type: "video/mp4",
                  isExisting: true,
                  serverUrl: url,
                  isUploaded: true,
                });
              });
            }

            setSelectedImages(savedFiles?.selectedImages ?? []);
            setUploadedImageFiles(existingImageFiles);
            setUploadedVideoFiles(existingVideoFiles);
            setPrompt(savedFiles?.prompt ?? "");
            setAudioPrompt(savedFiles?.audioPrompt ?? "");
            setTaskName(savedFiles?.taskName ?? model.name ?? "");
            setAreDataLoadedFromLocalStorage(true);
            setVideoDuration(savedFiles?.videoLength ?? 5);
            setIs4kDataset(Boolean(savedFiles?.is4kDataset));
          }
        } else if (trainingType === "image") {
          if (!areDataLoadedFromLocalStorage) {
            if (savedFiles?.datasetPairs) {
              const pairs = savedFiles.datasetPairs.map((pair, index) => ({
                id: pair.id || Date.now() + index,
                before: pair.before
                  ? {
                      id: `saved-before-${index}`,
                      file: null,
                      name: `Dataset ${index + 1}`,
                      type: "image/jpeg",
                      isExisting: true,
                      serverUrl: pair.before,
                      isUploaded: true,
                    }
                  : null,
                after: pair.after
                  ? {
                      id: `saved-after-${index}`,
                      file: null,
                      name: `Control ${index + 1}`,
                      type: "image/jpeg",
                      isExisting: true,
                      serverUrl: pair.after,
                      isUploaded: true,
                    }
                  : null,
              }));
              setDatasetPairs(
                pairs.length > 0
                  ? pairs
                  : [
                      { id: Date.now() + 1, before: null, after: null },
                      { id: Date.now() + 2, before: null, after: null },
                      { id: Date.now() + 3, before: null, after: null },
                    ],
              );
            }

            // Restore testing images
            const existingImageFiles = [];
            if (savedFiles?.imagesUrl) {
              savedFiles.imagesUrl.forEach((url, index) => {
                existingImageFiles.push({
                  id: `saved-image-${index}`,
                  file: null,
                  name: `Image ${index + 1}`,
                  type: "image/jpeg",
                  isExisting: true,
                  serverUrl: url,
                  isUploaded: true,
                });
              });
            }

            setUploadedImageFiles(existingImageFiles);
            setSelectedImages(savedFiles?.selectedImages ?? []);
            setPrompt(savedFiles?.prompt ?? "");
            setTaskName(savedFiles?.taskName ?? model.name ?? "");
            setAreDataLoadedFromLocalStorage(true);
          }
        } else if (trainingType === "person") {
          if (!areDataLoadedFromLocalStorage) {
            const existingPersonImages = [];
            if (savedFiles?.personImagesUrl) {
              savedFiles.personImagesUrl.forEach((url, index) => {
                existingPersonImages.push({
                  id: `saved-person-${index}`,
                  file: null,
                  name: `Image ${index + 1}`,
                  type: "image/jpeg",
                  isExisting: true,
                  serverUrl: url,
                  isUploaded: true,
                });
              });
            }
            setPersonImages(existingPersonImages);
            setPrompt(savedFiles?.prompt ?? "");
            setTaskName(savedFiles?.taskName ?? model.name ?? "");
            setAreDataLoadedFromLocalStorage(true);
          }
        }
      }
    }
  }, [
    selectedTrainModel.data,
    selectedTrainModel.trainModelId,
    trainModelId,
    trainingType,
    areDataLoadedFromLocalStorage,
  ]);

  useEffect(() => {
    if (!selectedTrainModel.loading) {
      setTimeout(() => {
        setIsLoading(selectedTrainModel.loading);
      }, 300);
    } else {
      setIsLoading(true);
    }
  }, [selectedTrainModel.loading]);

  const handleBack = () => {
    navigate("/train");

    setTimeout(() => {
      setPrompt("");
      setAudioPrompt("");
      setTaskName("");
      setUploadedVideoFiles([]);
      setUploadedImageFiles([]);
      setIsDeleteModalOpen(false);
      setIsReadOnlyMode(false);
      setDatasetPairs([
        { id: Date.now() + 1, before: null, after: null },
        { id: Date.now() + 2, before: null, after: null },
        { id: Date.now() + 3, before: null, after: null },
      ]);
      setPersonImages([]);
    }, 300);
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
  };

  // =============================================
  // File upload logic (shared across modes)
  // =============================================

  const handleFileUpload = async (event, fileType) => {
    if (isReadOnlyMode) return;

    setIsMediaUploading(true);

    const input = event.target;
    const files = Array.from(input.files);

    const validFiles = files.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);
    const skippedFiles = files.length - validFiles.length;

    if (skippedFiles > 0) {
      toast.error(
        `${skippedFiles} file(s) were skipped because they exceed ${MAX_FILE_SIZE_MB} MB.`,
      );
    }

    if (validFiles.length === 0) {
      setIsMediaUploading(false);
      return;
    }

    const newFiles = await Promise.all(
      validFiles.map(async (file) => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        type: file.type,
        isExisting: false,
        isUploaded: false,
      })),
    );

    if (fileType === "image") {
      setUploadedImageFiles((prev) => {
        const updated = [...prev, ...newFiles];
        newFiles.forEach(({ file, type, id }) => {
          uploadDataToStorage(file, type, id);
        });
        return updated;
      });
    } else if (fileType === "video") {
      setUploadedVideoFiles((prev) => {
        const updated = [...prev, ...newFiles];
        newFiles.forEach(({ file, type, id }) => {
          uploadDataToStorage(file, type, id);
        });
        return updated;
      });
    }

    setIsMediaUploading(false);
    if (input) input.value = "";
  };

  const handleUploadVideos = () => {
    if (isReadOnlyMode || isMediaUploading) return;

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "video/mp4";
    input.onchange = (e) => handleFileUpload(e, "video");
    input.click();
  };

  const handleSelectImages = () => {
    if (isReadOnlyMode) return;

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/jpeg, image/png";
    input.onchange = (e) => handleFileUpload(e, "image");
    input.click();
  };

  const handleRemoveFile = (fileId, fileType) => {
    if (isReadOnlyMode) return;

    if (fileType === "video") {
      setUploadedVideoFiles((prev) => prev.filter((f) => f.id !== fileId));
    } else if (fileType === "image") {
      setUploadedImageFiles((prev) => prev.filter((f) => f.id !== fileId));
    }
  };

  // =============================================
  // Dataset pair logic (Train Image mode)
  // =============================================

  const handleAddDatasetRow = () => {
    if (isReadOnlyMode || datasetPairs.length >= MAX_DATASET_ROWS) return;
    setDatasetPairs((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), before: null, after: null },
    ]);
  };

  const handleRemoveDatasetRow = (pairId) => {
    if (isReadOnlyMode) return;
    setDatasetPairs((prev) => prev.filter((p) => p.id !== pairId));
  };

  const handleDatasetFileUpload = (pairId, slot) => {
    if (isReadOnlyMode) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/jpeg, image/jpg, image/png";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File exceeds ${MAX_FILE_SIZE_MB} MB limit.`);
        return;
      }

      const fileObj = {
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        type: file.type,
        isExisting: false,
        isUploaded: false,
      };

      // Set the file in the pair immediately (shows spinner)
      setDatasetPairs((prev) =>
        prev.map((p) => (p.id === pairId ? { ...p, [slot]: fileObj } : p)),
      );

      // Upload to storage
      try {
        const parts = file.type.split("/");
        const fileType = parts[0] || null;
        const extension = parts[1] || null;

        const { uploadUrl, fileUrl } = await modelService.getUploadUrl({
          trainModelId,
          fileType,
          extension,
        });

        const uploadResponse = await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
            "Content-Disposition": "inline",
            "Cache-Control": "public, max-age=2592000, immutable",
          },
          body: file,
        });

        if (!uploadResponse.ok) throw new Error("Upload failed");

        // Mark as uploaded
        setDatasetPairs((prev) =>
          prev.map((p) =>
            p.id === pairId
              ? {
                  ...p,
                  [slot]: {
                    ...p[slot],
                    isUploaded: true,
                    serverUrl: fileUrl,
                  },
                }
              : p,
          ),
        );
      } catch (error) {
        toast.error(error.message);
        // Remove the failed file from the pair
        setDatasetPairs((prev) =>
          prev.map((p) => (p.id === pairId ? { ...p, [slot]: null } : p)),
        );
      }
    };
    input.click();
  };

  const handleRemoveDatasetFile = (pairId, slot) => {
    if (isReadOnlyMode) return;
    setDatasetPairs((prev) =>
      prev.map((p) => (p.id === pairId ? { ...p, [slot]: null } : p)),
    );
  };

  // =============================================
  // Person mode images logic
  // =============================================

  const handlePersonImageUpload = () => {
    if (isReadOnlyMode || isMediaUploading) return;

    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/jpeg, image/png, image/webp";
    input.onchange = async (e) => {
      setIsMediaUploading(true);
      const files = Array.from(e.target.files);
      const validFiles = files.filter(
        (file) => file.size <= MAX_FILE_SIZE_BYTES,
      );
      const skippedFiles = files.length - validFiles.length;

      if (skippedFiles > 0) {
        toast.error(
          `${skippedFiles} file(s) were skipped because they exceed ${MAX_FILE_SIZE_MB} MB.`,
        );
      }

      if (validFiles.length === 0) {
        setIsMediaUploading(false);
        return;
      }

      const newFiles = validFiles.map((file) => ({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        type: file.type,
        isExisting: false,
        isUploaded: false,
      }));

      setPersonImages((prev) => {
        const updated = [...prev, ...newFiles];
        newFiles.forEach(({ file, type, id }) => {
          uploadPersonImageToStorage(file, type, id);
        });
        return updated;
      });

      setIsMediaUploading(false);
    };
    input.click();
  };

  const uploadPersonImageToStorage = async (file, mimeType, id) => {
    try {
      const parts = mimeType.split("/");
      const fileType = parts[0] || null;
      const extension = parts[1] || null;

      const { uploadUrl, fileUrl } = await modelService.getUploadUrl({
        trainModelId,
        fileType,
        extension,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mimeType,
          "Content-Disposition": "inline",
          "Cache-Control": "public, max-age=2592000, immutable",
        },
        body: file,
      });

      if (!uploadResponse.ok) throw new Error("Upload failed");

      setPersonImages((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, isUploaded: true, serverUrl: fileUrl } : f,
        ),
      );
    } catch (error) {
      toast.error(error.message);
      setPersonImages((prev) => prev.filter((f) => f.id !== id));
    }
  };

  const handleRemovePersonImage = (fileId) => {
    if (isReadOnlyMode) return;
    setPersonImages((prev) => prev.filter((f) => f.id !== fileId));
  };

  // =============================================
  // Delete / Start Training
  // =============================================

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteTrainModel(trainModelId)).unwrap();
      navigate("/train");
      localStorage.setItem("activeTabIndex", "2");
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } catch (error) {
      console.error("Failed to delete train model:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleStartTraining = async () => {
    try {
      const payload = {
        trainModelId,
        name: taskName,
        prompt,
      };

      if (trainingType === "video") {
        const newVideoFiles = uploadedVideoFiles
          .filter((f) => f.isUploaded && f.serverUrl)
          .map((f) => f.serverUrl.split("?")[0]);

        const newImageFiles = uploadedImageFiles
          .filter((f) => f.isUploaded && f.serverUrl)
          .map((f) => f.serverUrl.split("?")[0]);

        payload.videosUrl = newVideoFiles;
        payload.imagesUrl = [...selectedImages, ...newImageFiles];
        payload.videoLength = videoDuration;
        payload.audioPrompt = audioPrompt;
        if (user?.isVipCreator && is4kDataset) {
          payload.is4kDataset = true;
        }
      } else if (trainingType === "image") {
        // Guard: every pair that has any file must be fully filled
        const incompletePairs = datasetPairs.filter(
          (p) =>
            (p.before || p.after) &&
            !(p.before?.isUploaded && p.after?.isUploaded),
        );
        if (incompletePairs.length > 0) {
          toast.error(
            "Each dataset pair must have both a Before and After image uploaded.",
          );
          return;
        }

        const completePairs = datasetPairs.filter(
          (p) => p.before?.isUploaded && p.after?.isUploaded,
        );

        payload.inputControlImagesUrl = completePairs.map(
          (p) => p.before.serverUrl.split("?")[0],
        );
        payload.inputImagesUrl = completePairs.map(
          (p) => p.after.serverUrl.split("?")[0],
        );

        const newImageFiles = uploadedImageFiles
          .filter((f) => f.isUploaded && f.serverUrl)
          .map((f) => f.serverUrl.split("?")[0]);

        payload.imagesUrl = [...selectedImages, ...newImageFiles];
      } else if (trainingType === "person") {
        payload.personImagesUrl = personImages
          .filter((f) => f.isUploaded && f.serverUrl)
          .map((f) => f.serverUrl.split("?")[0]);
      }

      dispatch(updateTrainModel(payload))
        .unwrap()
        .then(() => {
          navigate("/train");
        })
        .catch((err) => {
          console.log(err);
          toast.error(err);
          setIsReadOnlyMode(false);
        });
    } catch (error) {
      console.error("Failed to start training:", error);
    }
  };

  // =============================================
  // Upload helpers (video mode)
  // =============================================

  const uploadDataToStorage = async (file, mimeType, id) => {
    try {
      const parts = mimeType.split("/");
      const fileType = parts[0] || null;
      const extension = parts[1] || null;

      const { uploadUrl, fileUrl } = await modelService.getUploadUrl({
        trainModelId,
        fileType,
        extension,
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": mimeType,
          "Content-Disposition": "inline",
          "Cache-Control": "public, max-age=2592000, immutable",
        },
        body: file,
      });

      if (!uploadResponse.ok) throw new Error("Upload failed");

      markFileAsUploaded(fileType, id, fileUrl);
    } catch (error) {
      toast.error(error.message);
      const parts = mimeType.split("/");
      const fileType = parts[0] || null;
      removeFile(fileType, id);
    }
  };

  const markFileAsUploaded = (fileType, id, fileUrl) => {
    if (fileType === "image") {
      setUploadedImageFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, isUploaded: true, serverUrl: fileUrl } : f,
        ),
      );
    } else {
      setUploadedVideoFiles((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, isUploaded: true, serverUrl: fileUrl } : f,
        ),
      );
    }
  };

  const removeFile = (fileType, id) => {
    if (fileType === "image") {
      setUploadedImageFiles((prev) => prev.filter((f) => f.id !== id));
    } else {
      setUploadedVideoFiles((prev) => prev.filter((f) => f.id !== id));
    }
  };

  // =============================================
  // Validation for Start Training button
  // =============================================

  const isStartDisabled = () => {
    if (updateTrainModelLoading) return true;
    if (taskName.length < 3) return true;

    // Prompt is required for video and image, but NOT for person
    if (trainingType !== "person" && prompt.length < 100) return true;

    if (trainingType === "video") {
      const uploadedImgCount =
        uploadedImageFiles.filter((f) => f.isUploaded).length +
        selectedImages.length;
      const uploadedVidCount = uploadedVideoFiles.filter(
        (f) => f.isUploaded,
      ).length;
      return (
        uploadedImgCount < 3 ||
        uploadedImgCount > 6 ||
        uploadedVidCount < 10 ||
        uploadedVidCount > 20
      );
    }

    if (trainingType === "image") {
      const completePairs = datasetPairs.filter(
        (p) => p.before?.isUploaded && p.after?.isUploaded,
      ).length;
      const hasIncompletePairs = datasetPairs.some(
        (p) =>
          (p.before || p.after) &&
          !(p.before?.isUploaded && p.after?.isUploaded),
      );
      const testingImgCount =
        uploadedImageFiles.filter((f) => f.isUploaded).length +
        selectedImages.length;
      return (
        completePairs < 15 ||
        completePairs > 50 ||
        hasIncompletePairs ||
        testingImgCount < 3 ||
        testingImgCount > 6
      );
    }

    if (trainingType === "person") {
      const uploadedCount = personImages.filter((f) => f.isUploaded).length;
      return uploadedCount < 20 || uploadedCount > 40;
    }

    return false;
  };

  // =============================================
  // Loading state
  // =============================================

  if (isLoading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoaderColumn>
          <ModelTitle>New Template</ModelTitle>

          <Row>
            <SkeletonSection $height="fit-content">
              <SectionHeader>
                <SectionTitle>Configuration</SectionTitle>
                <SettingsSvg width={24} height={24} />
              </SectionHeader>
              <SectionWrapper>
                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
                <SkeletonLoader
                  $height="17px"
                  $width="100px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
                <SkeletonLoader
                  $height="17px"
                  $width="80px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader
                  $height="44px"
                  $width="100%"
                  $radius="12px"
                  style={{ marginBottom: -8 }}
                />
                <SkeletonLoader $height="15px" $width="70%" $radius="12px" />
              </SectionWrapper>
            </SkeletonSection>

            <SkeletonSection $height="fit-content">
              <SectionHeader>
                <SectionTitle>Requirements</SectionTitle>
                <CardIconSvg />
              </SectionHeader>
              <SectionWrapper>
                <LabelColumn>
                  <LabelContainer>
                    <Label>Training Videos</Label>
                    <UploadCount>10-15 videos</UploadCount>
                  </LabelContainer>
                  <Text>
                    Upload 10-15 videos showing the motion you want to train.
                    Each video should be between 3 to 5 seconds long, with a
                    minimum resolution of 360p.
                  </Text>
                </LabelColumn>

                <LabelColumn>
                  <LabelContainer>
                    <Label>Testing Images</Label>
                    <UploadCount>3-6 images</UploadCount>
                  </LabelContainer>
                  <Text>
                    Select 3-6 images from the library to test the trained
                    model.
                  </Text>
                </LabelColumn>

                <LabelColumn>
                  <LabelContainer>
                    <Label>Cost</Label>
                    <UploadCount>
                      <StarSvg />
                      {!user?.hasTrainedModel
                        ? "300 Credits (67% Off)"
                        : user?.isVipCreator || user?.numOfAcceptedModels > 15
                          ? "500 Credits"
                          : "900 Credits"}
                    </UploadCount>
                  </LabelContainer>
                  <Text>
                    Training cost is deducted when you start the training. Each
                    user has only one opportunity for a discounted trial.
                  </Text>
                </LabelColumn>
              </SectionWrapper>
            </SkeletonSection>
          </Row>

          <SkeletonSection $height="fit-content">
            <SectionHeader>
              <LabelRow>
                <LabelUpload>Training Videos</LabelUpload>
                <UploadCount>0/20</UploadCount>
              </LabelRow>
              <VideoSvg />
            </SectionHeader>
            <SectionWrapper>
              <SkeletonLoader $height="200px" $width="100%" $radius="16px" />
            </SectionWrapper>
          </SkeletonSection>

          <SkeletonSection $height="fit-content">
            <SectionHeader>
              <LabelRow>
                <LabelUpload>Training Images</LabelUpload>
                <UploadCount>0/6</UploadCount>
              </LabelRow>
              <ImageSvg />
            </SectionHeader>

            <SectionWrapper>
              <SkeletonLoader $height="200px" $width="100%" $radius="16px" />
            </SectionWrapper>
          </SkeletonSection>

          <ActionButtons>
            <SkeletonLoader $height="40px" $width="130px" $radius="100px" />
            <SkeletonLoader $height="40px" $width="160px" $radius="100px" />
          </ActionButtons>
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  // =============================================
  // Counts
  // =============================================

  const totalVideoCount = uploadedVideoFiles.length;
  const totalImageCount = uploadedImageFiles.length + selectedImages.length;
  const totalDatasetCount = datasetPairs.filter(
    (p) => p.before?.isUploaded || p.after?.isUploaded,
  ).length;
  const totalPersonImageCount = personImages.length;

  // =============================================
  // Render: Requirements section content by type
  // =============================================

  const renderRequirements = () => {
    if (trainingType === "video") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>
          <SectionWrapper>
            <TutorialLink onClick={handleTutorialClick}>
              <LinkText>📚 View our training tutorial to get started.</LinkText>
              <ChevronLeft />
            </TutorialLink>

            <LabelColumn>
              <LabelContainer>
                <Label>Training Videos</Label>
                <UploadCount>10-20 videos</UploadCount>
              </LabelContainer>
              <Text>
                Upload 10-20 videos showing transition cuts from still picture
                to motion video. Each video should be between 3 to 5 seconds
                long, with a minimum resolution of 360p.
              </Text>
            </LabelColumn>

            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {!user?.hasTrainedModel
                    ? "300 Credits (67% Off)"
                    : user?.isVipCreator || user?.numOfAcceptedModels > 15
                      ? "500 Credits"
                      : "900 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training. Each user
                has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }

    if (trainingType === "image") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>
          <SectionWrapper>
            <LabelColumn>
              <LabelContainer>
                <Label>Dataset</Label>
                <UploadCount>15-50 pairs</UploadCount>
              </LabelContainer>
              <Text>
                Upload 15-50 before &amp; after examples of images. Each pair
                should contain a dataset image and a corresponding control
                image.
              </Text>
            </LabelColumn>
            <LabelColumn>
              <LabelContainer>
                <Label>Testing Images</Label>
                <UploadCount>3-6 images</UploadCount>
              </LabelContainer>
              <Text>
                Select 3-6 images from the library to test the trained template.
              </Text>
            </LabelColumn>
            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {"400 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training.
                <br />
                Each User has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }

    if (trainingType === "person") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>

          <SectionWrapper>
            <LabelColumn>
              <LabelContainer>
                <Label>Images</Label>
                <UploadCount>20-40 photos</UploadCount>
              </LabelContainer>
              <Text>
                Upload 20-40 high quality photos of the same person from
                different angles.
              </Text>
            </LabelColumn>

            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {!user?.hasTrainedModel
                    ? "300 Credits (67% Off)"
                    : user?.isVipCreator || user?.numOfAcceptedModels > 15
                      ? "500 Credits"
                      : "900 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training.
                <br />
                Each User has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }
  };

  // =============================================
  // Render: Dataset section (Train Image mode)
  // =============================================

  const renderDatasetSection = () => (
    <>
      <UploadSection>
        <SectionHeader ref={datasetHeaderRef}>
          <DatasetHeaderLeft>
            <LabelUpload>Dataset</LabelUpload>
            <UploadCount>
              {totalDatasetCount}/15-{MAX_DATASET_ROWS}
            </UploadCount>
          </DatasetHeaderLeft>
        </SectionHeader>

        <SectionWrapper>
          <DatasetGrid>
            {datasetPairs.map((pair, index) => (
              <DatasetRow key={pair.id}>
                <DatasetRowNumber>{index + 1}</DatasetRowNumber>

                <DatasetRowContent>
                  {/* BEFORE column */}
                  <DatasetColumn>
                    <DatasetColumnLabel>Before</DatasetColumnLabel>
                    <DatasetUploadCard
                      $disabled={isReadOnlyMode}
                      onClick={() =>
                        !pair.before &&
                        !isReadOnlyMode &&
                        handleDatasetFileUpload(pair.id, "before")
                      }
                    >
                      {pair.before ? (
                        <>
                          {!pair.before.isUploaded ? (
                            <l-line-spinner
                              size="30"
                              stroke="2"
                              speed="1"
                              color="#fff"
                            />
                          ) : (
                            <img
                              src={pair.before.serverUrl}
                              alt={pair.before.name}
                            />
                          )}
                          {!isReadOnlyMode && (
                            <DatasetRemoveFileButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDatasetFile(pair.id, "before");
                              }}
                            >
                              <LottieIcon
                                animationData={deleteAnimation}
                                trigger="hover"
                                size={20}
                                absolute
                                strokeColor={"#FF8787"}
                              />
                            </DatasetRemoveFileButton>
                          )}
                        </>
                      ) : (
                        <>
                          <UploadSvg />
                          <DatasetUploadText>
                            {"Upload Photo"}
                          </DatasetUploadText>
                          <DatasetUploadSubtext>
                            or drag and drop
                          </DatasetUploadSubtext>
                        </>
                      )}
                    </DatasetUploadCard>
                  </DatasetColumn>

                  {/* Arrow */}
                  <DatasetArrow>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M21 12L14 19L14 15L3.8 15C3.51997 15 3.37996 15 3.273 14.9455C3.17892 14.8976 3.10243 14.8211 3.0545 14.727C3 14.62 3 14.48 3 14.2L3 9.8C3 9.51997 3 9.37996 3.0545 9.27301C3.10243 9.17892 3.17892 9.10243 3.27301 9.0545C3.37996 9 3.51997 9 3.8 9L14 9L14 5L21 12Z"
                        stroke="#2877D9"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </DatasetArrow>

                  {/* AFTER column */}
                  <DatasetColumn>
                    <DatasetColumnLabel>After</DatasetColumnLabel>
                    <DatasetUploadCard
                      $disabled={isReadOnlyMode}
                      onClick={() =>
                        !pair.after &&
                        !isReadOnlyMode &&
                        handleDatasetFileUpload(pair.id, "after")
                      }
                    >
                      {pair.after ? (
                        <>
                          {!pair.after.isUploaded ? (
                            <l-line-spinner
                              size="30"
                              stroke="2"
                              speed="1"
                              color="#fff"
                            />
                          ) : (
                            <img
                              src={pair.after.serverUrl}
                              alt={pair.after.name}
                            />
                          )}
                          {!isReadOnlyMode && (
                            <DatasetRemoveFileButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDatasetFile(pair.id, "after");
                              }}
                            >
                              <LottieIcon
                                animationData={deleteAnimation}
                                trigger="hover"
                                size={20}
                                absolute
                                strokeColor={"#FF8787"}
                              />
                            </DatasetRemoveFileButton>
                          )}
                        </>
                      ) : (
                        <>
                          <UploadSvg />
                          <DatasetUploadText>
                            {"Upload Photo"}
                          </DatasetUploadText>
                          <DatasetUploadSubtext>
                            or drag and drop
                          </DatasetUploadSubtext>
                        </>
                      )}
                    </DatasetUploadCard>
                  </DatasetColumn>
                </DatasetRowContent>

                {/* Remove row button */}
                {!isReadOnlyMode && datasetPairs.length > 1 && (
                  <DatasetRemoveRowButton
                    onClick={() => handleRemoveDatasetRow(pair.id)}
                    title="Remove row"
                  >
                    {/* <CloseSvg width={14} height={14} /> */}

                    <LottieIcon
                      animationData={deleteAnimation}
                      trigger="hover"
                      size={20}
                      absolute
                      strokeColor={"#FF8787"}
                    />
                  </DatasetRemoveRowButton>
                )}
              </DatasetRow>
            ))}
            {!isReadOnlyMode && (
              <AddSetContainer
                title="Add Another Set"
                onClick={handleAddDatasetRow}
                disabled={datasetPairs.length >= MAX_DATASET_ROWS}
              >
                <PlusSvg />
                Add Another Set
              </AddSetContainer>
            )}
          </DatasetGrid>
        </SectionWrapper>
      </UploadSection>
    </>
  );

  // =============================================
  // Render: Video upload sections (Train Video mode)
  // =============================================

  const renderVideoSections = () => (
    <>
      {/* Training Videos */}
      <UploadSection>
        <SectionHeader>
          <LabelRow>
            <LabelUpload>Training Videos</LabelUpload>
            <UploadCount>{totalVideoCount}/20</UploadCount>
          </LabelRow>
          <VideoSvg />
        </SectionHeader>
        <SectionWrapper>
          {totalVideoCount > 0 && (
            <FilePreviewGrid>
              {uploadedVideoFiles.map((fileObj) => (
                <FilePreviewItem id={fileObj.id} key={fileObj.id}>
                  <FilePreview>
                    {!fileObj.isUploaded ? (
                      <ImgContainer>
                        <l-line-spinner
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                          size="30"
                          stroke="2"
                          speed="1"
                          color="#fff"
                        />
                      </ImgContainer>
                    ) : (
                      <video
                        src={fileObj.serverUrl}
                        muted
                        controls
                        preload="metadata"
                        width="100%"
                        height="auto"
                      />
                    )}
                  </FilePreview>
                  {!isReadOnlyMode && (
                    <RemoveFileButton
                      onClick={() => handleRemoveFile(fileObj.id, "video")}
                    >
                      <CloseSvg width={12} height={12} />
                    </RemoveFileButton>
                  )}
                  <FileName>{fileObj.name}</FileName>
                </FilePreviewItem>
              ))}
            </FilePreviewGrid>
          )}

          {!isReadOnlyMode && (
            <UploadCard2 onClick={handleUploadVideos}>
              {isMediaUploading ? (
                <l-line-spinner size="30" stroke="2" speed="1" color="#fff" />
              ) : (
                <>
                  <UploadSvg />
                  <UploadText>Upload Videos</UploadText>
                  <UploadDescriptionText>
                    or drag and drop
                  </UploadDescriptionText>
                </>
              )}
            </UploadCard2>
          )}
        </SectionWrapper>
      </UploadSection>
    </>
  );

  // =============================================
  // Render: Testing Images section (shared: video + image)
  // =============================================

  const renderTestingImagesSection = () => (
    <UploadSection>
      <SectionHeader>
        <LabelRow>
          <LabelUpload>Testing Images</LabelUpload>
          <UploadCount>{totalImageCount}/3-6</UploadCount>
        </LabelRow>
        <ImageSvg />
      </SectionHeader>
      <SectionWrapper>
        {totalImageCount > 0 && (
          <FilePreviewGrid>
            {uploadedImageFiles.map((fileObj) => (
              <FilePreviewItem key={fileObj.id}>
                <FilePreview>
                  <ImgContainer>
                    {!fileObj.isUploaded ? (
                      <l-line-spinner
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                        size="30"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                    ) : (
                      <img src={fileObj.serverUrl} alt={fileObj.name} />
                    )}
                  </ImgContainer>
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() => handleRemoveFile(fileObj.id, "image")}
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
                <FileName>{fileObj.name}</FileName>
              </FilePreviewItem>
            ))}
            {selectedImages.map((url) => (
              <FilePreviewItem key={url}>
                <FilePreview>
                  <img src={url} alt={url} />
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() =>
                      setSelectedImages((prev) =>
                        prev.filter((itemUrl) => url !== itemUrl),
                      )
                    }
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
              </FilePreviewItem>
            ))}
          </FilePreviewGrid>
        )}

        {!isReadOnlyMode && (
          <UploadCard2>
            {isMediaUploading ? (
              <l-line-spinner size="30" stroke="2" speed="1" color="#fff" />
            ) : (
              <>
                <ImagesSvg />
                <UploadText onClick={() => setShowGalleryModal(true)}>
                  Select Images
                </UploadText>
                {user?.isVipCreator && (
                  <>
                    <UploadDescriptionText>or</UploadDescriptionText>
                    <UploadText onClick={handleSelectImages}>
                      Upload Images
                    </UploadText>
                    <UploadDescriptionText>
                      .png, .jpeg, .webp
                    </UploadDescriptionText>
                  </>
                )}
              </>
            )}
          </UploadCard2>
        )}
      </SectionWrapper>
    </UploadSection>
  );

  // =============================================
  // Render: Person images section
  // =============================================

  const renderPersonSection = () => (
    <UploadSection>
      <SectionHeader>
        <LabelRow>
          <LabelUpload>Images</LabelUpload>
          <UploadCount>{totalPersonImageCount}/40</UploadCount>
        </LabelRow>
        <ImageSvg />
      </SectionHeader>

      <SectionWrapper>
        {totalPersonImageCount > 0 && (
          <FilePreviewGrid>
            {personImages.map((fileObj) => (
              <FilePreviewItem key={fileObj.id}>
                <FilePreview>
                  <ImgContainer>
                    {!fileObj.isUploaded ? (
                      <l-line-spinner
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                        size="30"
                        stroke="2"
                        speed="1"
                        color="#fff"
                      />
                    ) : (
                      <img src={fileObj.serverUrl} alt={fileObj.name} />
                    )}
                  </ImgContainer>
                </FilePreview>
                {!isReadOnlyMode && (
                  <RemoveFileButton
                    onClick={() => handleRemovePersonImage(fileObj.id)}
                  >
                    <CloseSvg width={12} height={12} />
                  </RemoveFileButton>
                )}
                <FileName>{fileObj.name}</FileName>
              </FilePreviewItem>
            ))}
          </FilePreviewGrid>
        )}

        {!isReadOnlyMode && (
          <UploadCard2 onClick={handlePersonImageUpload}>
            {isMediaUploading ? (
              <l-line-spinner size="30" stroke="2" speed="1" color="#fff" />
            ) : (
              <>
                <UploadSvg />
                <UploadText>Upload Images</UploadText>
                <UploadDescriptionText>or drag and drop</UploadDescriptionText>
              </>
            )}
          </UploadCard2>
        )}
      </SectionWrapper>
    </UploadSection>
  );

  // =============================================
  // Main render
  // =============================================

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>New Template</ModelTitle>

      {/* Training progress (video mode only — adjust condition as needed) */}
      {["TRAINING", "TRAINING_COMPLETED", "GENERATING_COLLECTIONS"].includes(
        selectedTrainModel.data?.status,
      ) && (
        <TrainingProgressComponent
          progress={50}
          title="Training in Progress"
          subtitle="Training typically takes 24 hours to complete (maybe longer)."
        />
      )}

      <Row>
        <ConfigSection>
          <SectionHeader>
            <SectionTitle>Configuration</SectionTitle>
            <SettingsSvg width={24} height={24} />
          </SectionHeader>
          <SectionWrapper>
            {trainingType !== "video" && trainingType !== "image" && (
              <TutorialLink onClick={handleTutorialClick}>
                <LinkText>
                  📚 View our training tutorial to get started.
                </LinkText>
                <ChevronLeft />
              </TutorialLink>
            )}
            <InputContainer>
              <Label>Task name</Label>
              <TextInput
                type="text"
                placeholder="New Template 01"
                value={taskName}
                onChange={(e) => setTaskName(e.target.value)}
                disabled={isReadOnlyMode}
              />
            </InputContainer>

            {trainingType !== "person" && (
              <InputContainer>
                <LabelContainer>
                  <Label>Prompt</Label>
                  {prompt.length < 100 && (
                    <Label $light>{prompt.length}/100</Label>
                  )}
                </LabelContainer>
                <TextAreaInput
                  $outline={prompt.length !== 0 && prompt.length < 100}
                  type="text"
                  placeholder={
                    trainingType === "video"
                      ? "e.g. 'Start with a scene of a woman kneeling with her mouth wide open as multiple men cum on her face one after another. Watery cum drops land across her cheeks, nose and tongue. She keeps eyes closed and smiles happily as cum drips slowly.'"
                      : "e.g. 'This woman naked, kneeling with mouth wide open, hands cuffed behind forcing her forward in a dimly lit playroom.'"
                  }
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  disabled={isReadOnlyMode}
                />
                <RequirementText>
                  A sentence that clearly and accurately describes the motion in
                  your training videos. Prompt must contain at least 100
                  characters.
                </RequirementText>
              </InputContainer>
            )}

            {trainingType === "video" && (
              <InputContainer>
                <Label>Duration</Label>

                <Buttons
                  $activeIndex={activeIndex}
                  $count={DURATION_OPTIONS.length}
                >
                  {DURATION_OPTIONS.map((duration) => {
                    const isSelected = videoDuration === duration;

                    return (
                      <ButtonDuration
                        disabled={isReadOnlyMode}
                        key={duration}
                        $active={isSelected}
                        onClick={() => setVideoDuration(duration)}
                      >
                        {duration}s
                      </ButtonDuration>
                    );
                  })}
                </Buttons>

                <RequirementText $opacity>
                  Important: The vides you submit must be exactly{" "}
                  {videoDuration} seconds long.
                </RequirementText>

                {user?.isVipCreator && (
                  <CheckboxRow
                    $disabled={isReadOnlyMode}
                    onClick={() => {
                      if (isReadOnlyMode) return;
                      setIs4kDataset(!is4kDataset);
                    }}
                  >
                    <LottieIcon
                      animationData={checkboxAnimation}
                      trigger="toggle-reverse"
                      isActive={is4kDataset}
                      speed={2}
                      size={24}
                      strokeColor={"#2877d9"}
                    />
                    <CheckboxLabel>4k Dataset</CheckboxLabel>
                  </CheckboxRow>
                )}
              </InputContainer>
            )}
          </SectionWrapper>
        </ConfigSection>

        {renderRequirements()}
      </Row>

      {/* Upload sections based on training type */}
      {trainingType === "video" && renderVideoSections()}
      {trainingType === "image" &&
        !["TRAINING", "TRAINING_COMPLETED", "GENERATING_COLLECTIONS"].includes(
          selectedTrainModel.data?.status,
        ) &&
        renderDatasetSection()}
      {(trainingType === "video" || trainingType === "image") &&
        renderTestingImagesSection()}
      {trainingType === "person" && renderPersonSection()}

      {/* Action buttons */}
      <ActionButtons>
        {(!isReadOnlyMode || updateTrainModelLoading) && (
          <DeleteButton
            leftIcon={<TrashSvg width={15} height={15} />}
            title={"Delete"}
            style={{
              borderRadius: "100px",
              display: "flex",
              width: "130px",
              height: "44px",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#f2efe8",
              cursor: "pointer",
              fontFamily: "Figtree, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              letterSpacing: "-0.08px",
              transition: "all 0.3s ease-in-out",
            }}
            onClick={handleDelete}
            disabled={updateTrainModelLoading}
          />
        )}
        {(!isReadOnlyMode || updateTrainModelLoading) && (
          <Button
            onClick={handleStartTraining}
            disabled={isStartDisabled()}
            loading={updateTrainModelLoading}
            title={"Start Training"}
            leftIcon={<MagicStickSvg />}
          />
        )}
      </ActionButtons>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        isLoading={deleteTrainModelLoading}
        message="Are you sure you want to delete this train model? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      <AnonymizeModal
        isOpen={openAnonymizeModal}
        onClose={() => {
          setOpenAnonymizeModal(false);
          setCollectionGroup(null);
        }}
        collectionGroup={collectionGroup}
        collections={anonymizeSelectImages || []}
        select={collectionGroup}
        onSelect={(selected) => {
          setSelectedImages((prev) => {
            const newImages = selected.filter(
              (url) => !prev.some((u) => u.split("?")[0] === url.split("?")[0]),
            );
            return [...prev, ...newImages];
          });
        }}
      />
      <GalleryModal
        setOpenAnonymizeModal={setOpenAnonymizeModal}
        setOpenAnonymizeSelectModal={setAnonymizeSelectImages}
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        setCollectionGroup={setCollectionGroup}
        onConfirm={(images) => setSelectedImages(images)}
        selectedImages={selectedImages}
        uploadedImagesCount={uploadedImageFiles.length}
      />
    </MainContainer>
  );
};

export default TrainingPage;
